import { BodyCondition, EngineCondition, QuoteType } from "../../../models/Quote";

// Turns the API's calculation json (PascalCase, straight off CalculationLog) into rows a person
// can read. Every price type is kept, not just the one that won, so it is obvious *why* it won.

export type BreakdownSide = "collection" | "dropOff";

export interface BreakdownRow {
  name: string;
  /** How the amount was reached, e.g. "10% of £1,000.00" or "£180 * 1.527". */
  detail: string;
  /** Signed contribution to the price, null when the row carries no amount (e.g. a cap-only rule). */
  amount: number | null;
}

export interface BreakdownSection {
  type: QuoteType;
  title: string;
  rows: BreakdownRow[];
}

export interface Breakdown {
  sections: BreakdownSection[];
  /** Specific rules that nearly applied, with the one condition that stopped them. */
  skippedRules: string[];
  /** Fixed amount added on top for drop off. */
  dropOffFee: number;
  /** Rows added when the quote was accepted (payment method, collection option). */
  extras: BreakdownRow[];
}

interface RawCalculation {
  Name?: string;
  IsFixed?: boolean;
  Value?: number;
  BaseValue?: number;
  IsAddition?: boolean;
  CustomCalculation?: string;
  DropOff?: boolean;
}

const gbp = (value: number) =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(value);

export const formatPrice = (value: number | null | undefined) =>
  value == null || value < 0 ? "No price" : gbp(value);

export const quoteTypeLabel = (type: QuoteType) =>
  ({
    [QuoteType.Salvage]: "Salvage",
    [QuoteType.ScrapExport]: "Scrap Export",
    [QuoteType.SmartPrice]: "Smart Price",
  }[type] ?? "Unknown");

export const engineConditionLabel = (value: EngineCondition) =>
  ({
    [EngineCondition.NoFault]: "No fault (starts & drives)",
    [EngineCondition.MinorFault]: "Minor fault",
    [EngineCondition.MajorFault]: "Major fault",
  }[value] ?? String(value));

export const bodyConditionLabel = (value: BodyCondition) =>
  ({
    [BodyCondition.Good]: "Good",
    [BodyCondition.Average]: "Average",
    [BodyCondition.HeavyDamage]: "Heavy damage",
  }[value] ?? String(value));

export const parseJson = (json?: string | null): any | null => {
  if (!json) return null;
  try {
    return JSON.parse(json);
  } catch {
    return null;
  }
};

const isEmptyRow = (raw?: RawCalculation | null) => !raw || !raw.Name;

const toRow = (raw: RawCalculation): BreakdownRow => {
  const value = raw.Value ?? 0;
  const baseValue = raw.BaseValue ?? 0;
  const sign = raw.IsAddition === false ? -1 : 1;
  const custom = raw.CustomCalculation?.trim();

  if (custom) {
    // Postcode rows read "£180 * 1.527 (£274.86)" - the bracketed figure is the result.
    const result = custom.match(/\(£\s*(-?[\d,.]+)\)\s*$/);
    const amount = result ? parseFloat(result[1].replace(/,/g, "")) : value ? Math.abs(value) : null;
    return { name: raw.Name!, detail: custom, amount: amount == null ? null : sign * amount };
  }

  if (raw.IsFixed === false) {
    return {
      name: raw.Name!,
      detail: `${value}% of ${gbp(baseValue)}`,
      amount: sign * ((baseValue * value) / 100),
    };
  }

  return { name: raw.Name!, detail: "Fixed amount", amount: sign * Math.abs(value) };
};

/**
 * Some steps are logged separately for collection and drop off (postcode, additions, specific
 * rules); others only once and apply to both (trade poor, salvage deductions, catalytic). A list
 * that holds any drop-off rows is split by side, otherwise every row applies to both.
 */
const rowsForSide = (list: RawCalculation[] | undefined, side: BreakdownSide): BreakdownRow[] => {
  const items = (list ?? []).filter((x) => !isEmptyRow(x));
  const isSplit = items.some((x) => x.DropOff === true);
  const wantDropOff = side === "dropOff";

  return items.filter((x) => !isSplit || (x.DropOff ?? false) === wantDropOff).map(toRow);
};

const single = (raw: RawCalculation | undefined, side: BreakdownSide) =>
  isEmptyRow(raw) ? [] : rowsForSide([raw!], side);

export const buildBreakdown = (json: string | null | undefined, side: BreakdownSide): Breakdown | null => {
  const raw = parseJson(json);
  if (!raw) return null;

  const sections: BreakdownSection[] = [
    {
      type: QuoteType.Salvage,
      title: "Salvage",
      rows: [
        ...rowsForSide(raw.TradePoorCalculations, side),
        ...rowsForSide(raw.SalvageCalculations, side),
        ...single(raw.SalvageVoucherCalculation, side),
      ],
    },
    {
      type: QuoteType.ScrapExport,
      title: "Scrap Export",
      rows: [
        ...rowsForSide(raw.PostcodeCalculations, side),
        ...rowsForSide(raw.AdditionalCalculations, side),
        ...rowsForSide(raw.CatalyticCalculations, side),
        ...rowsForSide(raw.EngineCalculations, side),
        ...rowsForSide(raw.VfeCalculations, side),
        ...rowsForSide(raw.GeneralCalculations, side),
        ...rowsForSide(raw.GenericCalculations, side),
        ...single(raw.ScrapVoucherCalculation, side),
      ],
    },
    {
      type: QuoteType.SmartPrice,
      title: "Smart Price",
      rows: [
        ...rowsForSide(raw.PartsLookupCalculations, side),
        ...rowsForSide(raw.PartsLookupGenericCalculations, side),
      ],
    },
  ];

  const skippedRules = (raw.GenericSkippedCalculations ?? [])
    .filter((x: RawCalculation) => !isEmptyRow(x) && (x.DropOff ?? false) === (side === "dropOff"))
    .map((x: RawCalculation) => x.Name!);

  return {
    sections,
    skippedRules,
    dropOffFee: raw.DropOff ?? 0,
    extras: [...single(raw.Payment, side), ...single(raw.CollectionOption, side)],
  };
};
